
import React, { useCallback } from 'react';
import Icon from '@mdi/react';
import {
  mdiMapMarker,
  mdiMapMarkerRemoveVariant,
  mdiMapMarkerQuestion,
} from '@mdi/js';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { MdCheckCircleOutline, MdHelpOutline } from 'react-icons/md';
import { iconsList } from '../../constants/iconsList';
import {
  post, put, destroy, get, patch,
  capitalizeFirstLetter,
} from './index';
import {
  SITE_PRIORITY_HIGH,
  SITE_PRIORITY_MEDIUM,
  SITE_PRIORITY_LOW,
  SITE_PRIORITY_NO_PRIORITY,
  SITE_STATUS_ACTIVE,
  SITE_STATUS_INACTIVE,
  SITE_STATUS_REVIEW,
  ICONS_BASE,
} from '../../constants/sites';
import cleanUpObject from '../cleanUpObject';
import tailwindTheme from '../../../../../config/tailwind.config';

/**
   * Create a new site
   * @param {object} formData
   * @param {number} orgId organization id
   * @returns Promise with HTTP response regarding the new site
   * data is an object with the site key containing the site data as a value
   */

export const createSite = (formData, orgId) => {
  const data = { site: formData };
  return post(`/api/v3/sites?organization_id=${orgId}`, data);
};

/**
 * Update a site
 * @param {object} formData
 * @param {number} orgId
 * @param {number} siteId
 * @returns Promise with HTTP response regarding the updated site
 *
 */
export const updateSite = (formData, orgId, siteId, updateAddress = false) => {
  const data = { site: formData };
  return put(`/api/v3/sites/${siteId}?organization_id=${orgId}&update_address=${updateAddress}`, data);
};

export const getSiteInfo = (siteId, orgId) => get(`/api/v3/sites/${siteId}?organization_id=${orgId}`);
export const getSiteHistory = (siteId, orgId, pageNumber, limit) => get(`/api/v3/sites/${siteId}/site_history?organization_id=${orgId}&page=${pageNumber}&limit=${limit}`);
export const removeSite = (formData, siteId) => destroy(`/api/v3/sites/${siteId}`, formData);
// site contacts
export const addContactsToSite = (formData, siteId) => post(`/api/v3/sites/${siteId}/contacts`, formData);
export const addExistingContactToSite = (orgId, siteId, contactId) => post(
  `/api/v3/sites/${siteId}/contacts?organization_id=${orgId}`,
  {
    contacts: [{ id: contactId }],
  },
);
export const removeContactsFromSite = (formData, siteId) => destroy(`/api/v3/sites/${siteId}/contacts`, formData);
export const removeSiteFromContact = (contactId, siteId, orgId) => destroy(`/api/v3/sites/${siteId}/contacts?organization_id=${orgId}`, contactId);
export const getContactHistory = (siteId, orgId, pageNumber, limit) => get(`/api/v3/sites/${siteId}/contact_history?organization_id=${orgId}&page=${pageNumber}&limit=${limit}`);
// site images
export const addImageToSite = (formData, siteId, orgId) => post(`/api/v1/sites/${siteId}/images?organization_id=${orgId}`, formData);
// update image
export const updateImage = (orgId, siteId, imageId, newPosition) => patch(
  `/api/v2/sites/${siteId}/images/${imageId}`,
  { organization_id: orgId, new_position: newPosition },
);

// site notes
export const removeNote = (formData, noteId) => destroy(`/api/v1/notes/${noteId}`, formData);
// site visits
export const removeVisit = (formData, visitId) => destroy(`/api/v1/visits/${visitId}`, formData);
// site tags
export const removeTag = (tagId, siteId, orgId) => destroy(`/api/v3/tags/${tagId}/sites/${siteId}?organization_id=${orgId}`);
// site images
export const setPrimaryImage = (formData, siteId, id) => put(`/api/v1/sites/${siteId}/images/${id}`, formData);
export const removeImageFromSite = (siteId, id, orgId) => destroy(`/api/v1/sites/${siteId}/images/${id}?organization_id=${orgId}`);
// custom fields
export const updateCustomField = (formId, requestData) => put(`/api/v1/custom_fields/${formId}`, requestData);
export const removeCustomField = (itemId, formItemId) => destroy(`/api/v1/custom_fields/${itemId}`, formItemId);

/**
 * Fetch all sites available in Vamos
 * @param {number} orgId organization id
 * @returns Promise with HTTP response regarding all sites
 */
export const geth2aFormat = v => {
  if (v === 'Yes') return true;
  if (v === 'No') return false;
  if (v === 'No data') return 'nil';
  return v;
};
export const getSites = async (params) => {
  let queryParamString = { ...params, h2a: geth2aFormat(params.h2a) };
  cleanUpObject(queryParamString);
  queryParamString = new URLSearchParams(queryParamString).toString();
  return get(`/api/v3/sites?${queryParamString}`).then((response) => response.json());
};

/**
 * Returns map marker color based on provided priority params.
*/
export const getPriorityColor = (priority) => {
  switch (priority) {
    case SITE_PRIORITY_HIGH:
      return tailwindTheme.theme.extend.colors['priority-high'];
    case SITE_PRIORITY_MEDIUM:
      return tailwindTheme.theme.extend.colors['priority-medium'];
    case SITE_PRIORITY_LOW:
      return tailwindTheme.theme.extend.colors['priority-low'];
    case SITE_PRIORITY_NO_PRIORITY:
      return tailwindTheme.theme.extend.colors['vamos-gray'];
    default:
      return tailwindTheme.theme.extend.colors['vamos-gray'];
  }
};
/**
 * Returns Icon background color based on provided priority params.
*/
export const getPriorityClass = (priority) => {
  switch (priority) {
    case SITE_PRIORITY_HIGH:
      return 'priority-high';
    case SITE_PRIORITY_MEDIUM:
      return 'priority-medium';
    case SITE_PRIORITY_LOW:
      return 'priority-low';
    case SITE_PRIORITY_NO_PRIORITY:
      return 'vamos-gray';
    default:
      return 'vamos-gray';
  }
};

export const getSiteStatusColor = (status) => {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : status;
  switch (normalizedStatus) {
    case SITE_STATUS_ACTIVE:
      return tailwindTheme.theme.extend.colors['vamos-green1'];
    case SITE_STATUS_INACTIVE:
      return tailwindTheme.theme.extend.colors['vamos-red'];
    case SITE_STATUS_REVIEW:
      return tailwindTheme.theme.extend.colors['priority-medium'];
    default:
      return tailwindTheme.theme.extend.colors['vamos-green1'];
  }
};

/**
 * Returns an array of icon and color according to the site h2a status.
*/
export const getSiteH2AStatusMarker = (h2a) => {
  switch (h2a) {
    case true:
      return [MdCheckCircleOutline, tailwindTheme.theme.extend.colors['vamos-green1']];
    case null:
      return [MdHelpOutline, tailwindTheme.theme.extend.colors['priority-medium']];
    case false:
      return [IoCloseCircleOutline, tailwindTheme.theme.extend.colors['vamos-red']];
    default:
      return [MdHelpOutline, tailwindTheme.theme.extend.colors['priority-medium']];
  }
};

/**
 * Returns map marker path based on provided status params.
*/
const generateMapMarker = (status) => {
  switch (status) {
    case SITE_STATUS_ACTIVE:
      return mdiMapMarker;
    case SITE_STATUS_INACTIVE:
      return mdiMapMarkerRemoveVariant;
    case SITE_STATUS_REVIEW:
      return mdiMapMarkerQuestion;
    default:
      return mdiMapMarker;
  }
};

/**
 * Returns the mdi path that correspond to selected icon if any.
*/
export const getIcon = (icon) => {
  const iconObj = iconsList.filter((o) => o.value === icon);
  return iconObj.length > 0 ? iconObj[0].path : mdiMapMarker;
};

/**
 * Returns map marker based on provided priority, position, icon and status params.
*/

export const getMarker = ({
  priority, status, position, icon,
}) => {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : status;
  // Determine the path for the icon based on the status or the provided icon
  let iconData;
  if (icon && normalizedStatus === SITE_STATUS_ACTIVE) {
    // If there's an icon and status is active, use getIcon to find it
    const path = getIcon(icon) || generateMapMarker(normalizedStatus);
    iconData = { path };
  } else {
    // If no specific icon is provided, use the default path based on status
    iconData = { path: generateMapMarker(normalizedStatus) };
  }

  // Get the color for the priority
  const color = getPriorityColor(priority);

  // Add the color to the icon data
  iconData = { ...ICONS_BASE, fillColor: color, ...iconData };

  // If a position is provided, return an object with label and icon information
  if (position) {
    return {
      label: { text: position.toString(), fontWeight: 'bold' },
      icon: { ...iconData, labelOrigin: { x: 12, y: -2 } },
    };
  }

  // If no position is provided, return the icon data only
  return { ...ICONS_BASE, ...iconData };
};

/**
 * Returns Yes, No, No Value or Invalid based on the boolean value supposed to be provided.
*/
export const h2aOptions = [
  { name: 'No Data', value: '' },
  { name: 'Yes', value: true },
  { name: 'No', value: false },
];

export function getH2ANameFromH2AValue(value) {
  return h2aOptions.find((n) => n.value === value)?.name;
}

/**
 * this function is gets parameters list and texts values and returns text based on length of the list.
*/
export const returnTextBasedOnCount = (
  count,
  textForLengthIsOne = 'Item',
  textIsZero,
  textIsPlur,
) => {
  const textPlural = textIsPlur || `${count} ${textForLengthIsOne}s`;
  const textZero = textIsZero || `${textPlural}`;
  switch (count) {
    case 0:
      return textZero;
    case 1:
      return `${count} ${textForLengthIsOne}`;
    default:
      return textPlural;
  }
};

export const getTabsOrder = (orgId) => get(`/api/v3/app_data/?organization_id=${orgId}`);

export const updateTabsOrder = (orgId, tabsOrder) => put(`/api/v3/app_data/?organization_id=${orgId}`, {
  site: {
    tab_order: tabsOrder,
  },
});

/**
 * Formats the site data to be sent to the backend that handle unmodified status in add/edit form to be a number
 * @param {object} data
 * @param {array} statusOptions
 * @returns {object} formatted data
 */

export const formatSiteData = (data, statusOptions, initialState) => {
  const updatedData = Object.keys(initialState).reduce((acc, key) => {
    acc[key] = data[key] ?? initialState[key];
    return acc;
  }, {});

  // Since h2a is a boolean value and accept null value, we need to ensure it is set correctly
  updatedData.h2a = data.h2a;

  const formatted = { ...updatedData };

  // we need to send status as a number to the backend
  if (typeof data.status === 'string') {
    formatted.status = statusOptions.find((option) => option.label === data.status)?.value;
  } else {
    // updatedData.status is a string by default
    formatted.status = data.status;
  }

  // need to include primary contact id in contact_ids if it is not already in the list to allow backend to update primary contact
  if (data.primary_contact_id && !data.contact_ids?.includes(data?.primary_contact_id)) {
    formatted?.contact_ids?.push(data.primary_contact_id);
  }

  // we have different format for custom fields in get and put API calls, modifying the format here
  // additional code will be added during the implementation of custom fields
  if (data.custom_fields?.length > 0) {
    formatted.custom_fields = data.custom_fields?.map((field) => {
      return {
        form_item_id: field.key,
        position: field.position,
        value: field.value,
      };
    });
  }
  if (data.id) {
    formatted.site_id = data.id;
  }
  return formatted;
};

/**
 * Fetch site data in SiteForm and SiteShow components
 * @param {number} siteId
 * @param {number} organizationId
 * @param {function} setSiteData
 * @param {function} setInitialSiteData
 * @param {function} showToast
 * @param {function} setAssignedTags (optional) only for SiteForm component
 * @param {function} setPrimaryContactName (optional) only for SiteForm component
 * @returns setSiteData with site data
 */

export const useFetchSite = ({
  siteId, organizationId, setSiteData, setInitialSiteData, showToast, setAssignedTags, setPrimaryContactName, setSiteName,
}) => useCallback(() => {
  if (!siteId || !organizationId) return;
  getSiteInfo(siteId, organizationId)
    .then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }
      return response.json();
    })
    .then(data => {
      if (!data.site) {
        throw new Error(data.error);
      }
      // assign tags to display on the add/edit site form
      if (setAssignedTags && data.site.tags) {
        setAssignedTags(data.site.tags);
      }
      // set primary contact name to display on the add/edit site form
      if (setPrimaryContactName && data.primary_contact_id) {
        const contact = data.contacts?.find(({ id }) => id === data.primary_contact_id);
        setPrimaryContactName(contact?.name);
      }
      setSiteData({
        ...data.site,
        status: capitalizeFirstLetter(data.site?.status),
      });
      if (setInitialSiteData) {
        setInitialSiteData({
          ...data.site,
          status: capitalizeFirstLetter(data.site?.status),
        });
      }
      if (setSiteName) setSiteName(data.site.name);
    })
    .catch((error) => {
      showToast({ message: error.message, type: 'error' });
    });
}, [siteId, organizationId, showToast, setSiteData, setInitialSiteData, setAssignedTags, setPrimaryContactName, setSiteName]);

export const getHighestPriorityFromNotes = (notes = []) => {
  const priorities = notes.map((note) => note.priority);

  if (priorities.includes('high')) return 'high';
  if (priorities.includes('medium')) return 'medium';
  if (priorities.includes('low')) return 'low';
  return 'no_priority';
};

export const siteHeader = (site) => {
  return (
    <div className="flex flex-row justify-between items-center pt-3.5 pb-11.25">
      <div className="flex items-center">
        <div className={`flex items-center justify-center text-vamos-white w-9 h-9 mr-5.5 bg-${getPriorityClass(site.priority)} rounded-full`}>
          <Icon path={getIcon(site.icon ? site.icon : 'default')} size={1} />
        </div>
        <h2 className="font-sans text-32 font-bold text-vamos-black">{site.name}</h2>
      </div>
    </div>
  );
};
